/**
 * 作业录入的**纯规则**（家长端"录入这一天的作业"表单 ⇄ 整单保存入参）。
 *
 * ## 为什么放在 shared 而不是页面里
 *
 * 这里锁住的判定全都是"写错了不会报错、只会让用户觉得'保存不了'或'怎么没保存上'"的：
 *   - **删除的语义**：删掉一个科目 / 一个作业项之后，提交的 payload 里到底该有什么？
 *   - **什么才算错误**：哪些草稿行是"用户还没填的占位"，哪些是真正的输入错误。
 * 放在 shared 的零依赖纯函数里，家长端页面与测试引用的是**同一份**实现，
 * 不会出现"测试通过但线上走的是另一条分支"（与 `apps/api/.../homework.rules.ts` 同一套理由）。
 *
 * ## 删除的语义（**最终决定**，写在这里）
 *
 * 1. **草稿是"这一天应该长什么样"的声明，不是增量补丁**（与服务端 `PUT` 的整单语义一致）。
 *    因此"删掉"在草稿层就等于"提交时不再包含它" —— 不需要额外的删除接口。
 * 2. **一个没有任何作业项的科目 = 这一科从这天的作业里去掉**。
 *    为什么不做成"至少 1 项"的报错：用户按下「删掉」的意图就是不要它了，
 *    这时弹一句"每个科目至少要有 1 项作业"等于**用一条校验把用户的删除操作顶回去** ——
 *    这正是用户报的"删了以后保存不了"。真正的边界（有打卡记录的事实数据不能删）
 *    由服务端处理：那一科/那一项会被停用保留，并在响应里返回 `warnings` 让人看见。
 * 3. **完全没有内容的草稿行**（占位卡：没名字、没作业项）在提交时被丢弃，不算错误。
 * 4. **有作业项但没有名字的科目仍然是错误**：没有名字的科目在服务端会被 zod 拒绝，
 *    这是真正的输入错误（而不是删除），必须在本地就说清楚。
 *
 * ⚠️ 第 1 条有一个必须配对成立的**前提**：**表单里必须包含这一天计划里的全部内容**。
 * 否则"提交里没有"就同时意味着"用户想删"和"草稿构建漏了"，
 * 而这正是用户报的"**我只是加了一科，结果另一科被删掉**"（生产事故，2026-10-13）：
 * 打开「修改」时草稿漏掉了已有的一科 → 保存时 payload 里没有它 → 服务端按"移除"处理。
 *
 * 因此本文件里有两道**成对**的机制，改任何一半都必须同时看另一半：
 *   - `homeworkDayToDraft()`（读侧 → 草稿）：保证草稿**完整**，"没删过的东西必然在 payload 里"；
 *   - `homeworkPlanRemovals()`（保存前的语义护栏）：就算草稿将来又漏了什么，
 *     页面也会在保存前发现"这次会移除你没表达过要删的东西"并**拒绝直接保存**。
 *
 * ## 折叠字段（「时间」「更多」）与"隐藏 ≠ 没有值"
 *
 * 录入表单把「开始时刻 / 截止时刻 / 时长」与「分值 / 目标值 / 单位」**默认折叠**起来
 * （家长一般不用填这几项）。折叠只发生在本文件的**视图**层，规则层完全不参与：
 *   - 草稿的形状没有变：那些值照旧在 `HomeworkDraftSubject` / `HomeworkDraftItem` 里，
 *     `homeworkDraftToInput()` 也照旧把它们全部写进 payload；
 *   - 页面折叠起来的只是 `<input>`，**提交路径上没有任何"按可见性裁剪"的分支**；
 *   - 折叠时用 `describeSubjectScheduleInput()` / `describeItemPointsInput()` 把已有的值
 *     显示在那一行的摘要里，家长不会误以为"这一项没设过"。
 *
 * 回归测试见 `apps/api/src/__tests__/homework.draft.test.ts` 的「折叠字段」一节：
 * 逐条断言"折叠字段的值在转 payload 时**没有丢**"。
 *
 * ## 截止**日期**（这一节是 2026-10-16 新增的，与"截止时刻"是两件事）
 *
 * 用户的原始要求是"周五布置的作业，周末两天一夜内做完都不算超时"。改造前
 * 草稿里只有「起始时刻 / 截止时刻 / 时长」，**没有任何日期维度** ——
 * 截止日永远等于布置日（`checkin.due_at` 由「布置日 + `due_time`」推出），
 * 所以周五布置的作业周六 00:00 一过就超时。
 *
 * 现在草稿多一个 `dueDate`：
 *   - **它是具体日期字符串**（`'YYYY-MM-DD'`），不是"相对几天"——
 *     界面上显示的和提交出去的因此永远是同一个日子，不会出现"看着是周日、存下去是当天"；
 *   - **初值来自默认策略**（`defaultHomeworkDueDate()`，见 `homework-due.ts`）：
 *     周五/周六布置 → 周日，其余 → 当天。"默认值必须让家长一眼看见"，
 *     所以它是**真值**（填进输入框），而不是留空 + 一句提示；
 *   - **打开「修改」时原样回填**服务端存的那一天（老数据 = 布置日 = "当天完成"），
 *     **绝不**把默认策略重新算一遍：那等于"我什么都没改，保存一次截止日就变了"，
 *     正是"已有的作业不受影响"这条要求要挡住的事。
 *
 * ⚠️ `dueDate` 为空串只在一种情况下出现：**构建草稿时连布置日都不知道**
 * （页面永远会把布置日传进来，所以正常路径下它一定有值）。
 * 提交时它转成 `null`，服务端的语义是"与布置日同一天"（= 改造前的行为）。
 *
 * ## 分值的语义（见 `HOMEWORK_DEFAULT_POINTS_PER_UNIT`）
 *
 * 草稿里 `pointsPerUnit` 是**字符串**（输入框的值），转成数字时：
 *   - 空串 → `0`：用户明确留空 = "这一项不计分"（一个真实、稳定的值）；
 *   - 新草稿的初始值 = `HOMEWORK_DEFAULT_POINTS_PER_UNIT` 的**当前值 `0`** ——
 *     本项目的既定语义就是**作业项不计分**（`points_per_unit = 0` 是常态，不是漏填）。
 *     初值直接取常量、不写死数字，是为了让界面上的默认值与服务端落库口径永远是同一个数。
 *
 * ⚠️ 它与 `HabitFormModal` 里**普通打卡任务**的默认 `'1'` 不同 —— 那是有意为之：
 * 普通打卡任务默认 1 分，作业项默认不计分。
 */

import { HOMEWORK_DEFAULT_POINTS_PER_UNIT } from './constants.js';
import {
  HOMEWORK_MAX_DUE_DATE_DAYS,
  defaultHomeworkDueDate,
  describeHomeworkDueText,
  isLocalDateString,
} from './homework-due.js';
import { diffDays } from './time.js';

// ================================================================ 草稿模型

/** 编辑态的一条作业项。`key` 只给 React 用（草稿行可以重名、可以还没填名字）。 */
export interface HomeworkDraftItem {
  key: string;
  name: string;
  /** 输入框的原始字符串；空串 = 留空 = 不计分（转成 0）。 */
  pointsPerUnit: string;
  targetValue: string;
  unit: string;
}

/** 编辑态的一个科目。时刻三兄弟只留两个输入：截止时刻与时长（互斥）。 */
export interface HomeworkDraftSubject {
  key: string;
  name: string;
  startTime: string;
  dueTime: string;
  /** 空串 = 没填时长；填了时长时"截止时刻"必须留空（服务端也这么要求）。 */
  durationMinutes: string;
  /**
   * **截止日期**（'YYYY-MM-DD'）。
   *
   * ⚠️ 它与 `dueTime` 是两个维度：`dueDate` = 哪一天，`dueTime` = 那一天几点。
   * 初值是默认策略算出来的**具体日期**（见文件头"截止日期"一节），
   * 空串 = "连布置日都不知道"（正常路径不会出现，提交时按"同一天"处理）。
   */
  dueDate: string;
  items: HomeworkDraftItem[];
}

/** 整单保存的入参（与 `saveHomeworkSchema` 同形，但 `pointsPerUnit` 一定是具体数字）。 */
export interface HomeworkDraftPayload {
  subjects: {
    name: string;
    startTime: string | null;
    dueTime: string | null;
    durationMinutes: number | null;
    /** `null` = 与布置日同一天（服务端的兜底语义，也是改造前的行为）。 */
    dueDate: string | null;
    items: {
      name: string;
      pointsPerUnit: number;
      targetValue: number | null;
      unit: string | null;
    }[];
  }[];
}

/** 一条本地校验结果（界面上直接显示 `message`）。 */
export interface HomeworkDraftProblem {
  /** 出问题的科目在草稿里的下标（用于把提示挂在对应的卡片上）。 */
  subjectIndex: number;
  subjectName: string;
  message: string;
}

// ================================================================ 读侧视图（草稿的输入）
//
// ⚠️ 这几个类型是**服务端 `GET /students/:id/homework` 响应**里与"录入表单"有关的
// 子集（见 `apps/api/.../homework.service.ts` 的 `HomeworkDayResult`）。
// 刻意放在 shared 而不是页面里：`homeworkDayToDraft()` 是本文件里**唯一**决定
// "表单里能看到什么"的地方，它必须能被纯规则测试直接调用（见
// `apps/api/src/__tests__/homework.draft.test.ts` 的"表单完整性"一节）。

/** 科目截止时刻的来源（与后端 `HomeworkDueTimeSource` 同值）。 */
export type HomeworkDueTimeSource = 'explicit' | 'duration' | 'dayEnd' | 'none';

/** 视图里的一条作业项（只取草稿需要的字段）。 */
export interface HomeworkDraftItemView {
  name: string;
  pointsPerUnit: number;
  targetValue: number | null;
  unit: string | null;
  /** true = 已被移出这一天的计划（因有打卡记录而停用保留）。 */
  retained: boolean;
}

/** 视图里的一个科目（只取草稿需要的字段）。 */
export interface HomeworkDraftSubjectView {
  name: string;
  startTime: string | null;
  dueTime: string | null;
  durationMinutes: number | null;
  dueTimeSource: HomeworkDueTimeSource;
  /**
   * **截止日期**（'YYYY-MM-DD'）。
   *
   * ⚠️ 刻意是**可选**的：`__fixtures__/real-homework-day-2026-10-13.ts` 里那份
   * 真实响应是**在日期维度存在之前**捕获的，它没有这个字段（也**不该**被改写 ——
   * 那份夹具的价值就是"它逐字就是当时的响应"）。缺失/为空一律按**布置日**处理，
   * 也就是改造前的行为 —— 这正是"已有的作业不受影响"。
   */
  dueDate?: string | null;
  /** true = 整科被保留（其下所有项都已停用）。 */
  retained: boolean;
  items: HomeworkDraftItemView[];
}

/** 视图里的一天（只取草稿需要的字段）。 */
export interface HomeworkDraftDayView {
  /** 这一天是哪一天（作业单的 `start_date`）；缺省时草稿的截止日期回落到调用方给的值。 */
  date?: string;
  subjects: HomeworkDraftSubjectView[];
}

/**
 * 把服务端的截止时刻**回填成表单值**（编辑时必须用这个）。
 *
 * ⚠️ `dayEnd` 的 23:59 是服务端默认值：回填成空，让家长看到"这里本来就没填"。
 * 否则会出现"我明明没填截止时间，怎么自己变成了 23:59"，一改一动就把它固化下来。
 */
export function dueTimeInputOf(subject: {
  dueTime: string | null;
  dueTimeSource: HomeworkDueTimeSource;
}): string {
  if (subject.dueTimeSource === 'dayEnd' || subject.dueTimeSource === 'none') return '';
  return subject.dueTime ?? '';
}

// ================================================================ 构造

let draftSeq = 0;

/** 草稿行的 key（不能用名字当 key：草稿行可以重名、可以还没填名字）。 */
export function newHomeworkDraftKey(prefix: string): string {
  draftSeq += 1;
  return `${prefix}-${draftSeq}`;
}

/**
 * 一条新的作业项草稿。
 *
 * ⚠️ 分值默认取常量 `HOMEWORK_DEFAULT_POINTS_PER_UNIT` 的**当前值 `0` 分**，
 * 而不是空串：本项目的既定语义是**作业项不计分**（`points_per_unit = 0`），
 * 所以新作业项默认就是 0 分，这与"家长手动留空"是同一个值。
 * 之所以仍然走常量（而不是写死 `'0'`）：默认值只有一个来源，
 * 将来常量若要调整，草稿初值跟着变，不会出现"界面写着 0、服务端落别的数"。
 */
export function emptyHomeworkItemDraft(): HomeworkDraftItem {
  return {
    key: newHomeworkDraftKey('item'),
    name: '',
    pointsPerUnit: String(HOMEWORK_DEFAULT_POINTS_PER_UNIT),
    targetValue: '',
    unit: '',
  };
}

/**
 * 一个新的科目草稿（自带一行空作业项）。
 *
 * `planDate`（这一天的日期）给了就按**默认策略**算出截止日期填进去：
 * 周五/周六布置 → 周日，其余 → 当天（见 `homework-due.ts`）。
 * ⚠️ 默认值必须**填进输入框**（而不是留空 + 提示），因为要求是
 * "必须把默认值明确显示出来，让家长一眼看到这次作业截止到哪天"，
 * 也让"看到的"与"提交的"永远是同一个日子。
 *
 * 不传 `planDate`（纯规则测试、或调用方还没拿到日期）时 `dueDate` 是空串，
 * 提交时按"与布置日同一天"处理 —— 与改造前完全一致。
 */
export function emptyHomeworkSubjectDraft(planDate?: string): HomeworkDraftSubject {
  return {
    key: newHomeworkDraftKey('subject'),
    name: '',
    startTime: '',
    dueTime: '',
    durationMinutes: '',
    dueDate: planDate ? defaultHomeworkDueDate(planDate) : '',
    items: [emptyHomeworkItemDraft()],
  };
}

// ================================================================ 科目候选（"不用每次都手输"）
//
// ## 为什么候选来自**历史作业**而不是新建一张预设表
//
// 用户的要求是"科目先定义好，下拉选，不用每次手输"。最小代价的实现不是加表 + 迁移，
// 而是**把这一天之前录过的科目名收集起来当候选** —— 那些名字本来就已经躺在
// `node` 表里（`kind='homework'`、`depth=2` 的科目节点，按 `student_id` 过滤），
// 而且"以前录过"正是家长心里"我定义过的科目"的实际含义。
//
// 因此这里**没有新增表、没有迁移、也没有新增接口**：
//   - 数据来源 = 既有的 `GET /students/:id/homework/history`（只加了一个只读字段
//     `sheets[].subjects`，见 `homework.service.ts` 的注释）；
//   - 收集与排序 = 下面的纯函数（`collectHomeworkSubjectNames()`），
//     页面与测试引用同一份实现。
//
// ⚠️ 候选**只是建议**：下拉里永远有一个"输入新的科目…"，填新科目一直是允许的
// （新增科目是这个页面的主路径之一，不能被下拉堵死）。

/** 科目名在候选里的出现顺序（越靠前越"最近用过"）。 */
export interface HomeworkSubjectNameSource {
  /** 这一来源里的科目名（按"最近的在前"）。 */
  names: readonly string[];
}

/** 候选里最多留几个名字（下拉太长在手机上反而难选）。 */
export const HOMEWORK_SUBJECT_SUGGESTION_LIMIT = 12;

/** 科目名归一化：去首尾空白、内部连续空白压成一个空格（也去掉全角空格）。 */
export function normalizeHomeworkSubjectName(raw: string): string {
  return raw.replace(/[\s\u3000]+/g, ' ').trim();
}

/**
 * 收集下拉候选：**先今天/这一天的科目，再历史里最近录过的**，去重（忽略大小写）后限长。
 *
 * `sources` 的顺序就是优先级：调用方传 `[今天这一天的科目, ...历史各天]`。
 * 一天的科目里**保留项（retained）也照收** —— 那些名字家长以前真的用过，
 * 正是"想重新布置同名科目"时要选的那一个。
 */
export function collectHomeworkSubjectNames(
  sources: readonly HomeworkSubjectNameSource[],
  limit: number = HOMEWORK_SUBJECT_SUGGESTION_LIMIT,
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const source of sources) {
    for (const raw of source.names) {
      const name = normalizeHomeworkSubjectName(raw);
      if (name === '') continue;
      const key = name.toLocaleLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(name);
      if (out.length >= limit) return out;
    }
  }
  return out;
}

// ================================================================ 折叠区的摘要（**隐藏的是 UI，不是数据**）
//
// 家长要点一下才展开「时间」与「更多」。这带来一个必须显式解决的风险：
// **折叠着的字段如果不在摘要里显示，家长会以为"这一项没设过"**，
// 于是"保存一次就把看不见的值抹掉"这类事故就有了土壤（本项目刚因为
// "表单里没显示已有的科目 → 保存时把它删了"出过一次生产事故）。
//
// 所以折叠区一律配一行摘要，而且摘要走**这里**的纯函数：
//   - 有值 → 如实显示（`19:00 开始 · 20:00 截止`、`2 分 / 3 遍`）；
//   - 没值 → 显示这一刻的**默认口径**（`当天完成（不显示 23:59）`、`不计分`），
//     而不是留空让人猜。
//
// ⚠️ 摘要只看草稿里的输入值，因此它天然不会把服务端 `dayEnd` 的 23:59 当成
// 家长填过的时刻（回填用的是 `dueTimeInputOf()`，见上）。

/** 草稿里的这一科**填过时间**了吗（决定折叠区默认是展开还是收起）。 */
export function subjectScheduleIsSet(subject: {
  startTime: string;
  dueTime: string;
  durationMinutes: string;
}): boolean {
  return (
    subject.startTime.trim() !== '' ||
    subject.dueTime.trim() !== '' ||
    subject.durationMinutes.trim() !== ''
  );
}

/**
 * 折叠着的「时间」那一行摘要。
 *
 * ⚠️ 口径必须与 `describeSubjectSchedule()`（保存后的展示）一致：
 * 没填时刻 + 截止日就是布置日 = **当天完成**，绝不显示服务端落下的 23:59。
 *
 * `dueDate` / `planDate` 是 2026-10-16 新增的：截止日不再是布置日时，
 * 这一行必须说出**哪一天**（`周日 10-18 结束前完成`），否则摘要会撒谎 ——
 * 它会说"当天完成"，而这一天其实是周日。
 * 不传它们时（旧调用点与纯规则测试）行为与改造前逐字相同。
 */
export function describeSubjectScheduleInput(subject: {
  startTime: string;
  dueTime: string;
  durationMinutes: string;
  /** 草稿里的截止日期（'YYYY-MM-DD'）；省略 = 与布置日同一天。 */
  dueDate?: string | null;
  /** 这一天的日期（布置日）；省略时用 `dueDate`（两者相同 = "当天"）。 */
  planDate?: string;
}): string {
  const start = subject.startTime.trim();
  const due = subject.dueTime.trim();
  const duration = subject.durationMinutes.trim();
  const parts: string[] = [];
  if (start !== '') parts.push(`${start} 开始`);

  const dueText = describeHomeworkDueText({
    planDate: subject.planDate ?? subject.dueDate ?? '',
    dueDate: subject.dueDate ?? null,
    // 填了时长就是"开始 + 时长"派生截止（服务端同一条规则）
    dueTimeSource: duration !== '' ? 'duration' : due !== '' ? 'explicit' : 'dayEnd',
    dueTime: due === '' ? null : due,
    durationMinutes: duration === '' ? null : Number(duration),
  });

  /*
    ⚠️ 「当天完成（不显示 23:59）」这句**逐字保留**：它是 `dueTimeSource === 'dayEnd'`
    那条既有口径的用户侧说法（服务端落 23:59，但界面必须说"当天完成"）。
    跨天时改用带日期的说法（`周日 10-18 结束前完成`）—— 那时说"当天"才是错的。
  */
  parts.push(dueText === '' || dueText === '当天完成' ? '当天完成（不显示 23:59）' : dueText);
  return parts.join(' · ');
}

/**
 * 草稿里的这一项**填过**分值 / 目标值 / 单位了吗（决定折叠区默认展开还是收起）。
 *
 * ⚠️ `pointsPerUnit` 等于 0（含 `'0'` / `'0.0'`，转成 payload 后也是 0）算**没填**：
 * 用户明确说「作业目前我是不计分的」，所以新作业项的默认值（0 = 不计分）
 * 不该让「更多」自动弹开三行输入框。0 分的语义由摘要行说出来（「不计分」），
 * **不是**靠"默认展开"表达。
 */
export function itemExtraIsSet(item: {
  pointsPerUnit: string;
  targetValue: string;
  unit: string;
}): boolean {
  const points = item.pointsPerUnit.trim();
  const pointsGiven = points !== '' && Number(points) > 0;
  return pointsGiven || item.targetValue.trim() !== '' || item.unit.trim() !== '';
}

/**
 * 折叠着的「更多」那一行摘要（分值 / 目标值 / 单位）。
 *
 * 「不计分」是**要说出来**的：分值为 0 时家长点「通过」不会加分（服务端不写 0 分流水），
 * 摘要留空会让人以为"这里没设置过"，然后把"验收了怎么没加分"当成故障。
 */
export function describeItemPointsInput(item: {
  pointsPerUnit: string;
  targetValue: string;
  unit: string;
}): string {
  const points = item.pointsPerUnit.trim();
  const target = item.targetValue.trim();
  const unit = normalizeHomeworkSubjectName(item.unit);
  const parts: string[] = [];
  parts.push(points === '' ? '不计分' : `${points} 分`);
  if (target !== '') parts.push(`${target}${unit}`);
  return parts.join(' · ');
}

/**
 * 草稿是否"什么都没填"（用于「复制上次的结构」前的确认，以及"没什么可保存"的判断）。
 *
 * ⚠️ `dueDate` **不参与**这个判定，这是有意的：它**永远有值**（新建草稿时由默认策略
 * 填好），把它算成"内容"会让一张全新空表单被当成"你已经填过了" ——
 * 「复制上次的结构」于是每次都要多问一句，而那一句问的是不存在的内容。
 * 代价是"只改了截止日期、别的都没填"不算内容（复制会静默覆盖这个日期）：
 * 那是"一张空表单上的一个日期"，用户可以再点一次，比"每次都白问一句"划算。
 */
export function homeworkDraftIsEmpty(drafts: readonly HomeworkDraftSubject[]): boolean {
  return drafts.every(
    (subject) =>
      subject.name.trim() === '' &&
      subject.startTime === '' &&
      subject.dueTime === '' &&
      subject.durationMinutes === '' &&
      subject.items.every((item) => item.name.trim() === ''),
  );
}

// ================================================================ 「复制上次的结构」：覆盖前的确认
//
// ## 为什么这块判定住在 shared（而不是页面的 `if` 里）
//
// 「复制上次的结构」是**写入**（服务端直接落库，把这一天的科目与作业项换成"上一次"的结构），
// 于是它有两个都必须显式回答、而且答错了就会丢用户东西的问题：
//
//   1. **要不要先问一句？** 表单里已经有内容时（家长正在手输，或这一天本来就有作业），
//      这一下点击会把它整个覆盖掉 —— 而按钮就在录入区上方，手机上极易误触。
//   2. **"有内容"里有没有还没保存的输入？** 有的话，确认框必须**直说**这一点
//      （"你正在录入的内容会被丢弃"），不能只说"会覆盖这一天的作业"——
//      后者听起来像"改的是服务器上的东西"，家长不会意识到自己刚敲的字没了。
//
// 判定是纯函数，页面与测试引用**同一份**实现（与本文件其它判定的理由一样：
// 不会出现"测试通过但线上走的是另一条分支"）。
//
// ## 语义（**最终决定**，写在这里）
//
//   - `needsConfirm` = 草稿里**有内容**（`!homeworkDraftIsEmpty()`）。
//     刻意**不**收紧成"只有改过才问"：草稿里是这一天已有的作业时，复制同样会把它换掉，
//     那是真正会改变这一天计划的动作，值得一次明确的点头。
//   - `hasUnsavedChanges` = 表单里有**服务端还不知道**的输入。
//     它只用来**换确认框的文案**（把"你的输入会丢"说出来），不影响要不要问。
//
// ⚠️ 唯一的红线：`hasUnsavedChanges` 为 true 时**绝不能**静默执行 ——
// 用户刚敲进去的内容必须要么被保留、要么被明确告知将丢弃。

/** 「复制上次的结构」在覆盖之前要问什么。 */
export interface HomeworkCopyGate {
  /** 需要先确认（草稿里有内容，复制会把它换掉）。 */
  needsConfirm: boolean;
  /** 表单里有**还没保存**的输入 —— 复制会把它丢掉，确认框必须直说。 */
  hasUnsavedChanges: boolean;
}

/**
 * 草稿的**规范化签名**：走的是提交用的同一个转换（`homeworkDraftToInput()`），
 * 所以"看起来一样"与"提交出去一样"永远是同一件事 ——
 * 不会出现"界面上一模一样、签名却不同"从而误报"有未保存改动"。
 */
function homeworkDraftSignature(drafts: readonly HomeworkDraftSubject[]): string {
  return JSON.stringify(homeworkDraftToInput(drafts));
}

/**
 * 表单里有**服务端还不知道**的输入吗（= 有未保存的改动）。
 *
 * `source` 是**构建这份草稿时用的那份服务端视图**（页面里的 `draftSourceRef`）：
 *   - 它与草稿一致 → 家长还没改过什么（复制覆盖掉的只是"这一天本来就这样"）；
 *   - 它不存在（草稿不是从任何一次响应构建的）→ 有内容就算改动过，宁可多问一句。
 *
 * 一张全空的占位卡不算改动（与 `homeworkDraftIsEmpty()` 同一条口径：
 * 那是"还没开始填"，不是"用户表达了什么"）。
 */
export function homeworkDraftHasUnsavedChanges(
  drafts: readonly HomeworkDraftSubject[],
  source: HomeworkDraftDayView | undefined,
): boolean {
  if (homeworkDraftIsEmpty(drafts)) return false;
  return homeworkDraftSignature(drafts) !== homeworkDraftSignature(homeworkDayToDraft(source));
}

/**
 * 点「复制上次的结构」之前的判定（页面直接拿它决定"问不问、文案说什么"）。
 *
 * ⚠️ `source` 必须是**这份草稿的底稿**（页面的 `draftSourceRef.current`），
 * 不能传查询缓存里"现在"的那一份：后者可能已经在后台刷新过，
 * 拿它比会把家长刚敲的内容判成"服务端早就有了"，从而**少问一句**。
 */
export function homeworkCopyGate(
  drafts: readonly HomeworkDraftSubject[],
  source: HomeworkDraftDayView | undefined,
): HomeworkCopyGate {
  return {
    needsConfirm: !homeworkDraftIsEmpty(drafts),
    hasUnsavedChanges: homeworkDraftHasUnsavedChanges(drafts, source),
  };
}

// ================================================================ 读侧 → 草稿（**表单完整性**）
//
// ## 这一节修的是什么（用户实测报的那个数据丢失）
//
// 用户的真实操作：**先录入语文并保存 → 孩子点完成了一些 → 再点「修改」加数学
// → 表单里看不到语文 → 保存后语文被从当天的计划里移除了。**
//
// 根因就出在"打开「修改」时草稿里有什么"这一步。旧实现（写死在页面里）是：
//
//     const liveItems = subject.items.filter((item) => !item.retained);
//     if (liveItems.length === 0) return null;   // ← 整科返回 null = 这一科不进表单
//
// 于是"任一科里有保留项"这件事会通过两条路让整科从表单里消失：
//   1. 这一科**全部**项都是保留项 → 整科被丢掉（`null`）；
//   2. 表单里只剩"没被保留的项"，家长看到的科目内容与真实计划不一致。
// 而"保存 = 整单覆盖"意味着**表单里没有的科目 = 从这一天移除**：
// 服务端按第 2/3 条覆盖语义处理（有打卡记录 → 停用保留；没有 → 物理删除），
// 用户只是"加了一科"，却把另一科删掉了。
//
// ## 现在的语义（**最终决定**，与 `homeworkDraftToInput()` 的覆盖语义配套）
//
// 1. **仍然有效的科目与作业项一律进表单** —— 包括 `done` / `submitted` /
//    `skipped` / `running` 这些"状态不同"的项。它们**仍然是这一天计划的一部分**，
//    只是在表单里带着状态显示、可编辑范围可能不同（见下）。
//    ⚠️ 状态的过滤**不属于**这一层的职责：`homeworkDayToDraft()` 只看 `retained`。
// 2. **一个科目只要还在计划里就一定有草稿卡**，哪怕它下面的可见项暂时是 0 项
//    （旧实现在这里返回 `null`，正是数据丢失的入口）。这样家长永远能看到
//    "这一天现在有哪些科目"，而"删掉一科"只能通过**显式点「删除」**表达。
// 3. **`retained`（已移出计划）的项不进草稿** —— 它本来就不在这天的计划里，
//    再提交回去等于"加回来"。这一点保留旧行为，但：
//      - 它不再连带把整科从表单里删掉（第 2 条）；
//      - 页面必须把"这一科里有几项是保留记录"**显式写出来**，并把它渲染成
//        只读的备忘行（`retainedItems` / `retainedItemCount`），
//        绝不能让家长在一个"看起来什么都没有"的科目卡上点保存。
// 4. `retained` 的**整科**同样不进草稿（它已经不在计划里），页面在
//    「已移出计划的科目（保留记录）」区块里单独展示。
//
// 这一层是纯函数、零依赖，页面与测试引用的是**同一份**实现 —— 不会出现
// "测试通过但线上走的是另一条分支"。

/** 一个科目草稿 + 它"没进草稿"的那些保留项（供界面渲染成只读备忘行）。 */
export interface HomeworkSubjectDraftWithRetained {
  draft: HomeworkDraftSubject;
  /** 这一科里已被移出计划、因此没有进草稿的作业项名（保持读侧顺序）。 */
  retainedItems: string[];
}

/**
 * 服务端的科目 → 草稿（**永远返回一张卡**，见本节头注释第 2 条）。
 *
 * ⚠️ 三个"不照抄"的地方：
 *   - **截止时刻**用 `dueTimeInputOf()`：`dayEnd` 的 23:59 回填成**空**；
 *   - **截止日期原样回填**（`subject.dueDate`）：老数据 = 布置日 = "当天完成"，
 *     **绝不在打开表单时把默认策略重算一遍** —— 那等于"我什么都没改、一保存截止日就变了"；
 *   - **已落库的分值原样回显**：0 = 当初就是"不计分"，回填成空串再保存也还是 0，
 *     不会因为"编辑一次"就被悄悄改成默认分值。
 */
export function homeworkSubjectToDraft(
  subject: HomeworkDraftSubjectView,
  planDate?: string,
): HomeworkSubjectDraftWithRetained {
  const live = subject.items.filter((item) => !item.retained);
  const retainedItems = subject.items.filter((item) => item.retained).map((item) => item.name);
  /*
    截止日期：服务端给了就用它；没给（改造前捕获的真实响应夹具、或极老的接口）
    回落到布置日 —— 那正是改造前的行为（`due_at` = 布置日 + `due_time`）。
  */
  const dueDate = subject.dueDate ?? planDate ?? '';

  return {
    draft: {
      key: newHomeworkDraftKey('subject'),
      name: subject.name,
      startTime: subject.startTime ?? '',
      dueTime: dueTimeInputOf(subject),
      durationMinutes: subject.durationMinutes === null ? '' : String(subject.durationMinutes),
      dueDate,
      items: live.map((item) => ({
        key: newHomeworkDraftKey('item'),
        name: item.name,
        // 已经落库的分值原样回显：0 = 当初就是"不计分"，回填成空串再保存也还是 0。
        pointsPerUnit: item.pointsPerUnit > 0 ? String(item.pointsPerUnit) : '',
        targetValue: item.targetValue === null ? '' : String(item.targetValue),
        unit: item.unit ?? '',
      })),
    },
    retainedItems,
  };
}

/**
 * 服务端的一整天 → 草稿（打开「修改」时看到的**就是**这一份）。
 *
 * 保证（就是用户要的那一条）：**这一天仍然有效的科目与作业项，一个都不会缺席**。
 * 只有 `retained`（已移出计划）的东西不进草稿 —— 它们已在计划之外，
 * 且页面会把它们单独展示出来，不会让人误以为"什么都没了"。
 *
 * 一天里一个可录的科目都没有（全新的一天，或当天只剩保留项）→ 给一张空科目卡，
 * 它的截止日期按**默认策略**算（`planDate` / `day.date` 就是布置日）。
 */
export function homeworkDayToDraft(
  day: HomeworkDraftDayView | undefined,
  planDate?: string,
): HomeworkDraftSubject[] {
  const baseDate = day?.date ?? planDate;
  if (!day) return [emptyHomeworkSubjectDraft(baseDate)];
  const drafts = day.subjects
    .filter((subject) => !subject.retained)
    .map((subject) => homeworkSubjectToDraft(subject, baseDate).draft);
  return drafts.length > 0 ? drafts : [emptyHomeworkSubjectDraft(baseDate)];
}

// ================================================================ 表单内容快照（"我看到的就是这些"）
//
// ## 为什么还需要这一步（`homeworkDayToDraft()` 已经保证了完整性）
//
// 上面那一条保证有一个**没有写下来的前提**：`dayToDraft(day)` 里的那个 `day`
// 必须是**服务端此刻的真实数据**。只要它是旧的（React Query 的缓存、
// 上一次响应、页面在后台被失效过而刷新还没回来），表单就会少一科 ——
// 而"表单里没有的 = 从这一天移除"的方向是危险的那一边。
//
// 2026-10-13 生产库里发生的就是这个形状（审计日志 `audit_log` 还原）：
//
//   08:31:34  录入语文 4 项（新建作业单）
//   08:37:26  再点「修改」加数学 → 发出去的 payload 里**只有数学**
//             → 语文 2 项被停用保留、2 项被物理删除
//   09:18:26  家长把语文重新录了一遍（这次两科都在）
//
// 那一刻"表单里没有语文"这件事，光靠服务端返回的数据是解释不了的：
// 08:37 时语文的 4 项**全都是 active**（直到这次保存才被停用）。
// 也就是说表单当时拿到的不是这一天的真实数据，而是少了一科的旧数据。
// 所以光把草稿构建写完整**不够** —— 必须能在**发请求之前**发现
// "我现在这份草稿的底稿，与服务端此刻的计划不是同一份"。

/** 表单**底稿**的科目（只保留"这一天有哪些科、各有哪些项"这两件事）。 */
export interface HomeworkFormSnapshotSubject {
  name: string;
  items: string[];
}

/** 表单底稿的内容快照（按名称，与服务端整单覆盖的匹配口径一致）。 */
export interface HomeworkFormSnapshot {
  subjects: HomeworkFormSnapshotSubject[];
}

/**
 * 把"构建草稿时用的那份服务端视图"压成快照。
 *
 * ⚠️ 只保留 `retained === false` 的科目与作业项：它们是"这一天计划里的内容"，
 * 也正是"表单里没有就会被移除"的**危险集合**。保留项本来就不在计划里，
 * 进不进快照都不影响这次保存的后果。
 */
export function homeworkFormSnapshot(day: HomeworkDraftDayView | undefined): HomeworkFormSnapshot {
  return {
    subjects: (day?.subjects ?? [])
      .filter((subject) => !subject.retained)
      .map((subject) => ({
        name: subject.name.trim(),
        items: subject.items
          .filter((item) => !item.retained)
          .map((item) => item.name.trim()),
      })),
  };
}

/** 表单**底稿已经过期**：服务端此刻的计划里有草稿底稿里没有的内容。 */
export interface HomeworkFormDrift {
  /** 服务端现在有、而构建草稿时没有的科目名。 */
  newSubjects: string[];
  /** 服务端现在有、而构建草稿时没有的作业项（带所属科目名）。 */
  newItems: { subjectName: string; itemName: string }[];
}

/** 这次保存前必须先刷新（草稿底稿与服务端此刻的计划不一致）。 */
export function hasHomeworkFormDrift(drift: HomeworkFormDrift): boolean {
  return drift.newSubjects.length > 0 || drift.newItems.length > 0;
}

/**
 * 「保存前再看一眼服务端」：拿**构建草稿时用的那份视图**与**刚刚重新取的视图**对比，
 * 找出"服务端现在有、而表单底稿里没有"的内容。
 *
 * ⚠️ 方向刻意只有一边：这里**只**报"服务端多了什么"。
 * "表单里有、服务端没有"是另一回事（那是真的有人删掉了它），
 * 它由 `homeworkPlanRemovals()` 从"这次提交会移除什么"那一侧覆盖。
 * 两个方向都不做静默处理，但各管各的。
 */
export function homeworkFormDrift(
  snapshot: HomeworkFormSnapshot,
  current: HomeworkDraftDayView | undefined,
): HomeworkFormDrift {
  const snapshotSubjects = new Map<string, Set<string>>();
  for (const subject of snapshot.subjects) {
    snapshotSubjects.set(subject.name, new Set(subject.items));
  }

  const newSubjects: string[] = [];
  const newItems: { subjectName: string; itemName: string }[] = [];

  for (const subject of current?.subjects ?? []) {
    if (subject.retained) continue;
    const subjectName = subject.name.trim();
    const known = snapshotSubjects.get(subjectName);
    if (!known) {
      newSubjects.push(subjectName);
      continue;
    }
    for (const item of subject.items) {
      if (item.retained) continue;
      const itemName = item.name.trim();
      if (!known.has(itemName)) newItems.push({ subjectName, itemName });
    }
  }

  return { newSubjects, newItems };
}

/**
 * 把"草稿底稿已经过期"说成一句人话（页面直接弹出来）。
 *
 * 刻意把**后果**写在最前面：家长点「保存」时以为自己在"加一科"，
 * 而这一步拦下的正是"保存下去会把列表里没显示的东西删掉"。
 */
export function describeHomeworkFormDrift(drift: HomeworkFormDrift): string {
  const parts: string[] = [];
  if (drift.newSubjects.length > 0) {
    parts.push(`整个科目「${drift.newSubjects.join('」、「')}」`);
  }
  if (drift.newItems.length > 0) {
    const bySubject = new Map<string, string[]>();
    for (const entry of drift.newItems) {
      const list = bySubject.get(entry.subjectName);
      if (list) list.push(entry.itemName);
      else bySubject.set(entry.subjectName, [entry.itemName]);
    }
    for (const [subjectName, names] of bySubject) {
      parts.push(`${subjectName} 的作业项「${names.join('」、「')}」`);
    }
  }
  if (parts.length === 0) return '';
  return (
    `这一天现在还有${parts.join('、')}，但它们没有出现在你正在编辑的表单里` +
    `（表单打开之后这一天的作业变过）。刚才那一次保存没有发出去 —— ` +
    `表单已经按最新的作业刷新了，请确认后再点一次「保存」。`
  );
}

// ================================================================ 语义护栏：这次保存会移除什么
//
// ## 为什么需要它
//
// "整单覆盖"的语义是"草稿里没有的 = 从这一天移除"。这条语义本身没错
// （它让"删除"不需要第二个接口），但它有一个**危险的默认方向**：
// 只要草稿构建再漏掉任何一种分支，用户"只是加了一科"就会删掉另一科 ——
// 而且**不会报任何错**。用户的原话是"我只是加了一科，结果另一科没了"。
//
// 所以除了把草稿修完整（见上一节），这里再加一道**语义护栏**：
// 保存前拿"这一次要提交的入参"与"这一天当前计划里的科目与作业项"对一遍，
// 把**会被移除**的东西列出来。页面据此**拒绝直接保存并要一次明确确认**。
//
// 判据（与页面「删除」按钮的语义精确对齐）：
//   - 计划里的某个**科目名**在入参里完全找不到 → 这一科会被移除；
//   - 计划里某个科目下的某个**作业项名**在入参的同一科里找不到 → 这一项会被移除；
//   - 匹配按**名称**（与服务端 `applyHomeworkSave()` 的匹配口径一字不差）。
//     改名 = 删掉旧的 + 新建一个，所以"改名"同样会被列出来 —— 那是有意为之：
//     它会真的丢掉原记录，必须让用户看见并确认。
//
// ⚠️ 它**只看**"会不会移除"，不判对错、不做校验（校验见 `validateHomeworkDraft()`）。
// `retained` 的科目/作业项不在计划里，因此永远**不会**被列进"会被移除"。

/** 草稿操作：`draft` 里点「删除」按钮记录下来的名字（当前实现不需要它，保留给显式删除的场景）。 */
export interface HomeworkDraftIntent {
  /** 用户在表单里显式表达过"要删掉"的科目名（精确匹配）。 */
  removedSubjects?: readonly string[];
  /** 用户在表单里显式表达过"要删掉"的作业项名（按 `科目名 → 作业项名`）。 */
  removedItems?: readonly { subjectName: string; itemName: string }[];
}

/** 一次保存会从当天计划里移除的内容（科目 / 作业项，按名称）。 */
export interface HomeworkRemovalPreview {
  /** 会被整科移出计划的科目名。 */
  subjects: string[];
  /** 会被移出计划的作业项（带所属科目名）。 */
  items: { subjectName: string; itemName: string }[];
}

/** 这次保存是否会移除任何东西。 */
export function hasHomeworkRemovals(preview: HomeworkRemovalPreview): boolean {
  return preview.subjects.length > 0 || preview.items.length > 0;
}

/**
 * 「保存前对比」：这一次提交会让哪些**当前有效**的科目/作业项被移出计划。
 *
 * `current` 是服务端的当天视图（只统计 `retained === false` 的科目与项），
 * `payload` 是**即将提交**的入参（`homeworkDraftToInput()` 的结果）。
 * `intent`（可选）用来扣掉"用户明确表达过要删"的部分 —— 当前页面用不上，
 * 因为删除的唯一表达方式就是"草稿里不再包含它"，那时这里本来就会列出来。
 */
export function homeworkPlanRemovals(
  current: HomeworkDraftDayView | undefined,
  payload: HomeworkDraftPayload,
  intent: HomeworkDraftIntent = {},
): HomeworkRemovalPreview {
  const removedSubjectNames = new Set(intent.removedSubjects ?? []);
  const removedItemKeys = new Set(
    (intent.removedItems ?? []).map((entry) => itemKey(entry.subjectName, entry.itemName)),
  );

  const payloadBySubject = new Map<string, Set<string>>();
  for (const subject of payload.subjects) {
    const name = subject.name.trim();
    const existing = payloadBySubject.get(name) ?? new Set<string>();
    for (const item of subject.items) existing.add(item.name.trim());
    payloadBySubject.set(name, existing);
  }

  const subjects: string[] = [];
  const items: { subjectName: string; itemName: string }[] = [];

  for (const subject of current?.subjects ?? []) {
    if (subject.retained) continue;
    const subjectName = subject.name.trim();
    const submitted = payloadBySubject.get(subjectName);

    if (!submitted) {
      // 整个科目不在入参里 = 这一科会被移出计划
      if (!removedSubjectNames.has(subjectName)) subjects.push(subjectName);
      continue;
    }

    for (const item of subject.items) {
      if (item.retained) continue;
      const itemName = item.name.trim();
      if (submitted.has(itemName)) continue;
      if (removedItemKeys.has(itemKey(subjectName, itemName))) continue;
      items.push({ subjectName, itemName });
    }
  }

  return { subjects, items };
}

function itemKey(subjectName: string, itemName: string): string {
  return `${subjectName.trim()}\u0000${itemName.trim()}`;
}

/**
 * 把"会被移除的东西"说成一句人话（页面直接弹出、或放进确认框）。
 *
 * 刻意把"这次保存会做什么"写在最前面：家长点「保存」时的心理预期是"加一科"，
 * 而不是"删一科"，所以这句话必须把这个反差讲清楚。
 */
export function describeHomeworkRemovals(preview: HomeworkRemovalPreview): string {
  const parts: string[] = [];
  if (preview.subjects.length > 0) {
    parts.push(`整个科目「${preview.subjects.join('」、「')}」`);
  }
  if (preview.items.length > 0) {
    const bySubject = new Map<string, string[]>();
    for (const entry of preview.items) {
      const list = bySubject.get(entry.subjectName);
      if (list) list.push(entry.itemName);
      else bySubject.set(entry.subjectName, [entry.itemName]);
    }
    for (const [subjectName, names] of bySubject) {
      parts.push(`${subjectName} 的作业项「${names.join('」、「')}」`);
    }
  }
  if (parts.length === 0) return '';
  return `这次保存会把${parts.join('、')}从这一天的作业计划里移除`;
}

// ================================================================ 校验

/** 把用户填的字符串转成数字或 null（空串 = 没填，**不是 0**）。 */
function numberOrNull(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : Number.NaN;
}

/**
 * 保存前的本地校验。
 *
 * 刻意**只做便宜的那一半**：作业项名的必填、科目名的必填、科目重名、
 * 目标值与单位的配对、以及时刻"二选一"的两条结构规则（都是**一眼可见**的错）。
 * 服务端才是权威（`resolveSubjectSchedule`、"开始 + 时长不能跨过午夜"、
 * 有打卡记录只停用不删除），本地不重写那些规则。
 *
 * ⚠️ **"每个科目至少 1 项作业"不再是错误**：一个被删空的科目在
 * `homeworkDraftToInput()` 里会被丢掉，语义是"这一科不要了"（见文件头注释第 2 条）。
 *
 * `planDate`（这一天的日期）可选：给了就顺带查截止**日期**的两条一眼可见的错
 * （"不能早于布置日"、"别填到一年以后"）。服务端另有权威校验，
 * 见 `homework.rules.ts` 的 `resolveSubjectDueDate`。
 */
export function validateHomeworkDraft(
  drafts: readonly HomeworkDraftSubject[],
  planDate?: string,
): HomeworkDraftProblem[] {
  const problems: HomeworkDraftProblem[] = [];
  const seen = new Set<string>();

  for (const [index, subject] of drafts.entries()) {
    const name = subject.name.trim();
    const label = name || `第 ${index + 1} 个科目`;
    const items = subject.items.filter((item) => item.name.trim() !== '');

    // 没有作业项的科目 = 这一科不在这天的作业里（占位卡、或被用户删空）：不是错误。
    if (items.length === 0) continue;

    if (!name) {
      problems.push({ subjectIndex: index, subjectName: label, message: '请填写科目名称' });
    } else {
      const key = name.toLocaleLowerCase();
      if (seen.has(key)) {
        problems.push({
          subjectIndex: index,
          subjectName: name,
          message: `科目「${name}」重复了 —— 同名会被当成同一科，请改名或删掉一个`,
        });
      }
      seen.add(key);
    }

    /*
      截止**日期**的两条检查（"截止时刻"那两条在下面）。
      为什么不干脆交给服务端：这两个都是"手输/选错日期"造成的错，
      本地一句话就能说清，没必要让家长等一次网络往返才知道"保存不了"。
      格式检查放最前面：日期选择器给不出坏值，但粘贴与脚本可以。
    */
    const dueDate = subject.dueDate.trim();
    if (dueDate !== '') {
      if (!isLocalDateString(dueDate)) {
        problems.push({
          subjectIndex: index,
          subjectName: label,
          message: '截止日期格式应为 YYYY-MM-DD',
        });
      } else if (planDate && dueDate < planDate) {
        problems.push({
          subjectIndex: index,
          subjectName: label,
          message: `截止日期不能早于布置日（${planDate}）`,
        });
      } else if (planDate && diffDays(planDate, dueDate) > HOMEWORK_MAX_DUE_DATE_DAYS) {
        problems.push({
          subjectIndex: index,
          subjectName: label,
          message: `截止日期最多到布置日之后 ${HOMEWORK_MAX_DUE_DATE_DAYS} 天`,
        });
      }
    }

    const dueTime = subject.dueTime.trim();
    const durationRaw = subject.durationMinutes.trim();
    const duration = numberOrNull(durationRaw);
    if (durationRaw !== '' && (duration === null || Number.isNaN(duration) || duration <= 0)) {
      problems.push({ subjectIndex: index, subjectName: label, message: '时长要填大于 0 的分钟数' });
    }
    if (dueTime !== '' && durationRaw !== '') {
      problems.push({
        subjectIndex: index,
        subjectName: label,
        message: '截止时刻与时长只能填一个',
      });
    }
    if (durationRaw !== '' && subject.startTime.trim() === '') {
      problems.push({
        subjectIndex: index,
        subjectName: label,
        message: '填了时长就必须填开始时刻（时长是从开始时刻起算的）',
      });
    }

    for (const item of items) {
      const points = numberOrNull(item.pointsPerUnit);
      if (points !== null && (Number.isNaN(points) || points < 0)) {
        problems.push({ subjectIndex: index, subjectName: label, message: '分值要填不小于 0 的整数' });
      }
      const target = numberOrNull(item.targetValue);
      if (target !== null && (Number.isNaN(target) || target <= 0)) {
        problems.push({
          subjectIndex: index,
          subjectName: label,
          message: '目标值要大于 0，或者留空',
        });
      }
      if (target !== null && item.unit.trim() === '') {
        problems.push({
          subjectIndex: index,
          subjectName: label,
          message: `作业项「${item.name.trim() || '未命名'}」填了目标值就必须填单位`,
        });
      }
    }
  }

  return problems;
}

// ================================================================ 入参

/**
 * 草稿 → 整单保存的入参。
 *
 * 三条取舍（就是用户报的"删了以后保存不了"的修法）：
 *  1. **没有作业项的科目不进 payload** —— 这是"删除这一科"的表达方式；
 *     服务端会把它读成"这一天没有这一科"，于是有打卡记录的项被停用保留并给出 `warnings`。
 *  2. **没填名字的作业项直接丢掉**（不是错误，是"这一行不用了"）。
 *  3. 空分值 → `0`：留空是**明确的**"不计分"，与"没设置"（服务端落到
 *     `HOMEWORK_DEFAULT_POINTS_PER_UNIT`，**当前也是 0**）区分开
 *     —— 后者只发生在客户端根本没传这个字段时（见 `resolveHomeworkItemPoints`）。
 *     两条路径当前落出同一个数（0），但类型上仍然分开表达，别把它们合并掉。
 *
 * 全部科目都被删掉时返回 `{ subjects: [] }`：服务端的语义是"这一天不布置作业"
 * （连作业单节点一起清掉，GET 返回 `exists: false`）。
 *
 * ⚠️ **截止日期**：草稿里的值原样提交（`'YYYY-MM-DD'`）；空串 → `null`，
 * 服务端的语义是"与布置日同一天"（= 改造前的行为）。页面上它永远有值，
 * 所以"家长看到的那一天"与"存下去的那一天"是同一个。
 */
export function homeworkDraftToInput(
  drafts: readonly HomeworkDraftSubject[],
): HomeworkDraftPayload {
  const subjects: HomeworkDraftPayload['subjects'] = [];

  for (const subject of drafts) {
    const items = subject.items
      .filter((item) => item.name.trim() !== '')
      .map((item) => {
        const target = numberOrNull(item.targetValue);
        const points = numberOrNull(item.pointsPerUnit);
        return {
          name: item.name.trim(),
          pointsPerUnit: points === null || Number.isNaN(points) ? 0 : Math.trunc(points),
          targetValue: target === null || Number.isNaN(target) ? null : target,
          unit: item.unit.trim() === '' ? null : item.unit.trim(),
        };
      });

    if (items.length === 0) continue;

    const duration = numberOrNull(subject.durationMinutes);
    const dueDate = subject.dueDate.trim();
    subjects.push({
      name: subject.name.trim(),
      startTime: subject.startTime.trim() === '' ? null : subject.startTime.trim(),
      // ⚠️ 时长与截止时刻二选一：填了时长就把截止时刻置空，交由服务端派生
      dueTime: duration !== null || subject.dueTime.trim() === '' ? null : subject.dueTime.trim(),
      durationMinutes: duration === null ? null : Math.trunc(duration),
      // 空串 = 与布置日同一天（服务端的兜底语义，也是改造前的行为）
      dueDate: dueDate === '' ? null : dueDate,
      items,
    });
  }

  return { subjects };
}

/**
 * 「没填分值」时服务端该落多少分。
 *
 * ⚠️ 与"填了 0"在**入参边界上**是两件事（`pointsPerUnit` 是 `optional`，
 * 而不是 `.default(0)`）：
 *   - **没设置**（客户端没传这个字段）→ `HOMEWORK_DEFAULT_POINTS_PER_UNIT`（当前 **0 分**）；
 *   - **明确 0**  → `0`，语义是"这一项不计分"。
 * 本项目的既定语义是**作业项不计分**，所以常量就是 0 —— 两条路径当前落出**同一个数**，
 * 这个区分现在只体现在类型/入参上（将来常量若调整，它才有数值上的差别）。
 * 落库列 `habit.points_per_unit` 是 `integer NOT NULL`，**没有"未设置"这个状态**，
 * 所以这个区分只能在入参边界上做掉：进来的每一个作业项都必须变成一个具体的数字。
 */
export function resolveHomeworkItemPoints(pointsPerUnit: number | null | undefined): number {
  if (pointsPerUnit === null || pointsPerUnit === undefined) {
    return HOMEWORK_DEFAULT_POINTS_PER_UNIT;
  }
  if (!Number.isFinite(pointsPerUnit)) return HOMEWORK_DEFAULT_POINTS_PER_UNIT;
  return Math.max(0, Math.trunc(pointsPerUnit));
}
